import {
  AnomalyChart,
  DropDown,
  Flex,
  FlexColumn,
  FormControls,
  LoadingButton,
  TextField,
  ToggleButton,
} from '@elementary/common-ui/components';
import { AnomalyDetectionChart } from '@elementary/common-ui/types';
import { ToggleButtonGroup, Typography } from '@mui/material';
import { Controller, UseFormReturn } from 'react-hook-form';
import { anomalyDirectionOptions } from 'shared/constants';
import TimeWithUnitField from 'shared/components/TimeWithUnitField';
import {
  pipelineThresholdUnitOptions,
  sensitivityOptions,
  trainingPeriodOptions,
} from 'shared/constants';
import TrainingPeriodInfoTooltip from 'shared/components/TrainingPeriodInfoTooltip';
import { PipelineTaskPerformanceSimulateApi } from 'shared/types';
import { themeTokens } from '@elementary/common-ui/style/tokens';
import { space } from '@elementary/common-ui/style/utils';
import { PipelineTaskPerformanceTestConfig } from '../../types';

type Props = {
  methods: UseFormReturn<PipelineTaskPerformanceTestConfig>;
  readOnly?: boolean;
  onSimulate: (config: PipelineTaskPerformanceSimulateApi) => void;
  isSimulating: boolean;
  chartData: AnomalyDetectionChart | null;
};

const PipelineTaskPerformanceTestConfigurationFields = ({
  methods,
  readOnly,
  onSimulate,
  isSimulating,
  chartData,
}: Props) => {
  const mode = methods.watch('mode');
  const fixedThreshold = methods.watch('fixed_threshold');
  const isSimulateDisabled =
    mode === 'threshold' && !(fixedThreshold?.count && fixedThreshold?.period);

  const handleSimulate = () => {
    const {
      mode: currentMode,
      sensitivity,
      anomaly_direction,
      training_period,
      fixed_threshold,
    } = methods.getValues();
    onSimulate({
      type: 'pipeline_task_performance',
      config:
        currentMode === 'threshold'
          ? {
              mode: currentMode,
              fixed_threshold,
              ...(training_period ? { training_period } : {}),
            }
          : {
              mode: currentMode,
              sensitivity: sensitivity ?? 'medium',
              anomaly_direction: anomaly_direction ?? 'SPIKE',
              ...(training_period ? { training_period } : {}),
            },
    });
  };

  return (
    <>
      <FormControls.FieldsWrapper>
        <FlexColumn sx={{ gap: 2 }}>
          <FormControls.FormTitle>Detection mode</FormControls.FormTitle>
          <ToggleButtonGroup
            exclusive
            value={mode}
            disabled={readOnly}
            onChange={(_, val) => {
              if (!val) return;
              const current = methods.getValues();
              if (val === 'threshold') {
                methods.reset({
                  ...current,
                  mode: 'threshold',
                  fixed_threshold: current.fixed_threshold ?? {
                    count: 1,
                    period: 'hour',
                  },
                });
              } else {
                methods.reset({
                  ...current,
                  mode: 'anomaly',
                  sensitivity: current.sensitivity ?? 'medium',
                  anomaly_direction: current.anomaly_direction ?? 'SPIKE',
                });
              }
            }}
          >
            <ToggleButton value="anomaly" size="large" sx={{ width: '300px' }}>
              Anomaly detection
            </ToggleButton>
            <ToggleButton
              value="threshold"
              size="large"
              sx={{ width: '300px' }}
            >
              Static threshold
            </ToggleButton>
          </ToggleButtonGroup>
        </FlexColumn>
      </FormControls.FieldsWrapper>

      <FlexColumn
        sx={{ gap: 3, display: mode === 'anomaly' ? 'flex' : 'none' }}
      >
        <FormControls.FieldsWrapper style={{ gap: themeTokens.space.sm }}>
          <FormControls.FormTitle>Anomaly direction</FormControls.FormTitle>
          <FormControls.Row
            style={{ gap: themeTokens.space.sm, flexWrap: 'wrap' }}
          >
            <FormControls.FormText>Detect</FormControls.FormText>
            <Controller
              control={methods.control}
              name="anomaly_direction"
              render={({ field }) => (
                <DropDown
                  sx={{ minWidth: '160px' }}
                  sortOptions={false}
                  options={anomalyDirectionOptions}
                  {...field}
                  disabled={readOnly || field.disabled}
                />
              )}
            />
            <FormControls.FormText>
              {`in the task's run duration.`}
            </FormControls.FormText>
          </FormControls.Row>
        </FormControls.FieldsWrapper>

        <FormControls.FieldsWrapper style={{ gap: themeTokens.space.sm }}>
          <FormControls.FormTitle>Sensitivity</FormControls.FormTitle>
          <FormControls.Row
            style={{ gap: themeTokens.space.sm, flexWrap: 'wrap' }}
          >
            <FormControls.FormText>
              Adjust test thresholds by setting the sensitivity level to be
            </FormControls.FormText>
            <Controller
              control={methods.control}
              name="sensitivity"
              render={({ field }) => (
                <DropDown
                  sortOptions={false}
                  options={sensitivityOptions}
                  {...field}
                  disabled={readOnly || field.disabled}
                />
              )}
            />
          </FormControls.Row>
        </FormControls.FieldsWrapper>
      </FlexColumn>

      {mode === 'threshold' && (
        <FormControls.FieldsWrapper style={{ gap: themeTokens.space.sm }}>
          <FormControls.FormTitle>Threshold</FormControls.FormTitle>
          <Flex sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <FormControls.FormText>
              Alert when run duration exceeds
            </FormControls.FormText>
            <Controller
              control={methods.control}
              name="fixed_threshold"
              render={({ field }) => (
                <TimeWithUnitField
                  disabled={!!readOnly}
                  onChange={field.onChange}
                  count={methods.watch('fixed_threshold.count')}
                  period={methods.watch('fixed_threshold.period')}
                  options={pipelineThresholdUnitOptions}
                  defaultPeriod="hour"
                />
              )}
            />
          </Flex>
        </FormControls.FieldsWrapper>
      )}

      <FormControls.FieldsWrapper style={{ gap: themeTokens.space.sm }}>
        <Flex sx={{ alignItems: 'center', gap: space('xs') }}>
          <FormControls.FormTitle>Training period</FormControls.FormTitle>
          <TrainingPeriodInfoTooltip />
        </Flex>
        <FormControls.Row
          style={{
            gap: themeTokens.space.sm,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <Typography component="span">
            Base the model on the previous
          </Typography>
          <TextField
            sx={{ width: '70px', minWidth: '70px' }}
            type="number"
            disabled={readOnly}
            {...methods.register('training_period.count', {
              valueAsNumber: true,
            })}
          />
          <Controller
            control={methods.control}
            name="training_period.period"
            render={({ field }) => (
              <DropDown
                sortOptions={false}
                options={trainingPeriodOptions}
                sx={{ minWidth: '120px' }}
                {...field}
                disabled={readOnly || field.disabled}
              />
            )}
          />
        </FormControls.Row>
      </FormControls.FieldsWrapper>

      <FormControls.FieldsWrapper>
        {!readOnly && (
          <LoadingButton
            sx={{ width: 'fit-content' }}
            variant="outlined"
            loading={isSimulating}
            disabled={isSimulateDisabled}
            onClick={handleSimulate}
          >
            Simulate configuration
          </LoadingButton>
        )}

        {chartData && (
          <AnomalyChart
            metrics={chartData.metrics}
            status={chartData.status}
            metricsTitle={chartData.metricsTitle}
            anomalyDirection={chartData.anomalyDirection}
            detectionPeriod={chartData.detectionPeriod}
            detectionDelay={chartData.detectionDelay}
          />
        )}
      </FormControls.FieldsWrapper>
    </>
  );
};

export default PipelineTaskPerformanceTestConfigurationFields;
